import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { marketingApi } from '../marketing/marketingApi';

const ICON_PATHS = {
  home: (
    <>
      <path d="M3 11 12 3l9 8" />
      <path d="M5 10v10h5v-6h4v6h5V10" />
    </>
  ),
  notes: (
    <>
      <path d="M7 3h7l4 4v14H7z" />
      <path d="M14 3v5h5M10 13h5M10 17h5" />
    </>
  ),
  external: <path d="M14 4h6v6M20 4 10 14M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />,
  client: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M5 21a7 7 0 0 1 14 0" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  dollar: (
    <>
      <path d="M12 3v18" />
      <path d="M16 7.5c0-1.7-1.8-3-4-3s-4 1.3-4 3 1.8 3 4 3 4 1.3 4 3-1.8 3-4 3-4-1.3-4-3" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20a6 6 0 0 1 12 0M16 7a3 3 0 0 1 0 6M17 15a5 5 0 0 1 4 5" />
    </>
  ),
  megaphone: <path d="m4 13 12-6v10L4 13zM4 13v5h4v-3M16 10h3M18 6l2-2M18 18l2 2" />,
  post: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M8 9h8M8 13h8M8 17h5" />
    </>
  ),
  alert: (
    <>
      <path d="M12 3 2 20h20L12 3z" />
      <path d="M12 10v4M12 17h.01" />
    </>
  ),
  star: <path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2L12 17.3 6.4 20.2l1.1-6.2L3 9.6l6.2-.9z" />,
  chat: <path d="M4 5h16v11H9l-5 4z" />,
  mail: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </>
  ),
  list: <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />,
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </>
  ),
  image: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 16 5-5 4 4 3-3 6 6" />
      <circle cx="16" cy="9" r="1.5" />
    </>
  ),
  voice: <path d="M12 4v16M8 8v8M16 8v8M4 11v2M20 11v2" />,
};

export function NavIcon({ name, size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {ICON_PATHS[name]}
    </svg>
  );
}

// Progress notes, AI Agent Notes and AI Agent T&A stay in the separate notes app.
export const NOTES_APP_URL = process.env.REACT_APP_NOTES_APP_URL || 'https://notes.homecareofmi.com';

const ADMIN_GROUPS = [
  { label: 'Overview', items: [{ to: '/dashboard', icon: 'home', label: 'Dashboard', exact: true }] },
  {
    label: 'Care',
    items: [
      { to: '/clients', icon: 'client', label: 'Clients' },
      { to: '/authorizations', icon: 'shield', label: 'Prior Authorization' },
      { href: NOTES_APP_URL, icon: 'notes', label: 'Progress Notes app', external: true },
    ],
  },
  { label: 'Finance', items: [{ to: '/payments', icon: 'dollar', label: 'Payments' }] },
  {
    label: 'Marketing',
    items: [
      { to: '/marketing', icon: 'megaphone', label: 'Overview', exact: true },
      { to: '/marketing/posts', icon: 'post', label: 'Posts' },
      { to: '/marketing/escalations', icon: 'alert', label: 'Escalations', badgeKey: 'escalations' },
      { to: '/marketing/leads', icon: 'star', label: 'Leads', badgeKey: 'leads' },
      { to: '/marketing/comments', icon: 'chat', label: 'Comments' },
      { to: '/marketing/messages', icon: 'mail', label: 'Messages' },
      { to: '/marketing/brand-voice', icon: 'voice', label: 'Brand Voice' },
      { to: '/marketing/brand-images', icon: 'image', label: 'Brand Images' },
      { to: '/marketing/audit', icon: 'list', label: 'Audit Log' },
    ],
  },
  {
    label: 'System',
    items: [
      { to: '/users', icon: 'users', label: 'Users' },
      { to: '/settings', icon: 'settings', label: 'Settings' },
    ],
  },
];

function isActive(pathname, item) {
  if (!item.to) return false;
  if (item.exact) return pathname === item.to;
  return pathname === item.to || pathname.startsWith(item.to + '/');
}

export default function Sidebar({ collapsed, onToggle }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [badges, setBadges] = useState({});
  // Only administrators use this platform; caregivers are shown an admins-only page without the shell.
  const groups = ADMIN_GROUPS;

  // Marketing attention counts, refreshed every five minutes. Failures are silent.
  useEffect(() => {
    if (!user?.isAdmin) return undefined;
    let cancelled = false;
    const load = () => marketingApi.getDashboard()
      .then(d => { if (!cancelled) setBadges({ escalations: d.open_escalations || 0, leads: d.new_leads || 0 }); })
      .catch(() => {});
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user]);

  const initials = [user?.firstName?.[0], user?.lastName?.[0]].filter(Boolean).join('').toUpperCase() || '?';

  return (
    <aside className={`sidebar enterprise-sidebar${collapsed ? ' sidebar--collapsed' : ''}`}>
      <div className="sidebar-header enterprise-sidebar-header">
        <div className="sidebar-logo">
          <div className="enterprise-logo-mark">H</div>
          {!collapsed && (
            <div className="sidebar-brand-text">
              <span className="sidebar-brand-name">HCA Operations</span>
              <span className="sidebar-brand-sub">Home Care Associates</span>
            </div>
          )}
        </div>
        <button className="sidebar-toggle-btn" onClick={onToggle}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
          {collapsed ? '›' : '‹'}
        </button>
      </div>

      <nav className="sidebar-nav sidebar-nav-scroll" aria-label="Primary navigation">
        {groups.map(group => (
          <React.Fragment key={group.label}>
            <div className="sidebar-group">{collapsed ? '' : group.label}</div>
            {group.items.map(item => {
              if (item.external) {
                return (
                  <a key={item.href} href={item.href} target="_blank" rel="noreferrer"
                    className="sidebar-nav-item" title={collapsed ? item.label : 'Opens in a new tab'}>
                    <span className="sidebar-nav-icon"><NavIcon name={item.icon} /></span>
                    {!collapsed && <span className="sidebar-nav-label">{item.label}</span>}
                    {!collapsed && <span className="sidebar-nav-ext" aria-hidden="true"><NavIcon name="external" size={13} /></span>}
                  </a>
                );
              }
              const active = isActive(location.pathname, item);
              const badge = item.badgeKey ? badges[item.badgeKey] : 0;
              return (
                <Link key={item.to} to={item.to}
                  className={`sidebar-nav-item${active ? ' sidebar-nav-item--active' : ''}`}
                  title={collapsed ? item.label : undefined}>
                  <span className="sidebar-nav-icon"><NavIcon name={item.icon} /></span>
                  {!collapsed && <span className="sidebar-nav-label">{item.label}</span>}
                  {badge > 0 && <span className="sidebar-badge">{badge}</span>}
                </Link>
              );
            })}
          </React.Fragment>
        ))}
      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-user-row">
          <div className="sidebar-avatar">{initials}</div>
          {!collapsed && (
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{user?.firstName} {user?.lastName}</div>
              <div className="sidebar-user-role">{user?.isAdmin ? 'Administrator' : 'Caregiver'}</div>
            </div>
          )}
        </div>
        <button className={`sidebar-logout${collapsed ? ' sidebar-logout--icon' : ''}`} onClick={logout} title="Sign out">
          {collapsed ? '↩' : 'Sign out'}
        </button>
      </div>
    </aside>
  );
}
